import React, { useMemo } from 'react';
import { useFreelancerPageContext } from '../components/FreelancerPageContext';
import { fireClickEvents } from './fire-click-events';
import { firePageViewEvents } from './fire-page-view-events';
import { PAGE_NAME, Providers } from './monitoring-constants';
import { BQPageViewOptions } from './big-query';
import { MPPageViewOptions } from './mixpanel';
import { GrafanaOptions } from './grafana';
import { ContactSellerTargetType } from './types';

interface PageViewEventsMap {
    bigQuery?: BQPageViewOptions;
    mixpanel?: MPPageViewOptions;
    grafana?: GrafanaOptions;
}

export const useMonitoring = (eventsMap?: PageViewEventsMap) => {
    const { seller } = useFreelancerPageContext();
    const pageName = PAGE_NAME;

    React.useEffect(() => {
        if (seller && eventsMap) {
            firePageViewEvents({ seller })(eventsMap);
        }

        // We need to ignore the ESLint rule to enable the consumer to
        // pass us an object inline but only make one call.
        // Essentially, we don't want to send out events on re-renders, only on mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fireEvents = useMemo(() => {
        if (!seller) {
            return () => null;
        }

        return fireClickEvents({ seller, pageName });
    }, [seller, pageName]);

    const fireImpressionEvents = useMemo(() => {
        if (!seller) {
            return () => null;
        }

        return firePageViewEvents({ seller, pageName });
    }, [seller, pageName]);

    const handleClickContactButtonEvent = ({ source, componentName }: { source: string; componentName?: string }) => {
        fireEvents({
            [Providers.BigQuery]: {
                type: 'business_seller_page.contact_me',
                event_source: source,
                component_name: componentName,
                page: {
                    component: {
                        name: componentName,
                    },
                },
            },
            [Providers.Mixpanel]: {
                type: 'clicked contact me',
                source,
                component_name: componentName,
            },
        });
    };

    const handleContactButtonImpression = (elementName: ContactSellerTargetType) => {
        fireImpressionEvents({
            [Providers.BigQuery]: {
                type: 'contact_me_button_shown',
                element: {
                    type: 'contact_seller_only',
                    name: elementName,
                },
            },
        });
    };

    return {
        fireImpressionEvents,
        fireEvents,
        Providers,
        handleClickContactButtonEvent,
        handleContactButtonImpression,
    };
};
